export const SPLIT_ISSUE_UNDER = 1;
export const SPLIT_ISSUE_OVER = 2;
export const SPLIT_ISSUE_MERGE_LEFT = 4;
export const SPLIT_ISSUE_MERGE_RIGHT = 8;
export const SPLIT_ISSUE_MERGE_MASK = SPLIT_ISSUE_OVER | SPLIT_ISSUE_MERGE_LEFT | SPLIT_ISSUE_MERGE_RIGHT;
export const SPLIT_ISSUE_MASK_ALL = SPLIT_ISSUE_UNDER | SPLIT_ISSUE_MERGE_MASK;

export function normalizeUserAuthoredSplitIssueMask(issueMask: number) {
  const normalized = Number(issueMask || 0) & SPLIT_ISSUE_MASK_ALL;
  const explicitIssueMask = normalized & ~SPLIT_ISSUE_OVER;
  // legacy over는 방향을 알 수 없는 과거 경고다. 사용자가 새 의도를 고르면 함께 저장하지 않는다.
  return explicitIssueMask !== 0 ? explicitIssueMask : normalized;
}

export type ShotHelpFilterMode = "all" | "unidentified" | "review" | "ignored" | "over_split" | "under_split_candidate";
export type ActiveShotHelpFilterMode = Exclude<ShotHelpFilterMode, "all">;
export type BrowseShotHelpStateLike = "unidentified" | "user_need_to_help" | "system_need_to_adjust" | "resolved" | "ignored";
export type SplitHelpReviewOverride = {
  helpState: BrowseShotHelpStateLike;
  issueMask: number;
};

export type BrowseShotHelpRequestLike = {
  id: number;
  type: string;
  status: string;
  helpState: BrowseShotHelpStateLike;
  issueMask: number;
  systemIssueMask: number;
  userIssueMask: number | null;
  details: Record<string, unknown> | null;
};

export type BrowseShotLike = {
  shotId: number;
  videoId: number;
  shotNo: number;
  helpRequests: BrowseShotHelpRequestLike[];
};

export type ShotHelpSummary = {
  footer: {
    unidentified: number;
    review: number;
    ignored: number;
    over: number;
    under: number;
    applyGbdt: number;
  };
  states: Record<BrowseShotHelpStateLike, number>;
  requestBuckets: Record<string, number>;
};

export function isOverSplitHelpRequest(helpRequest: BrowseShotHelpRequestLike) {
  return helpRequest.type.startsWith("over_split");
}

export function splitIssueHasMerge(issueMask: number) {
  return (Number(issueMask || 0) & SPLIT_ISSUE_MERGE_MASK) !== 0;
}

export function helpRequestIssueMask(helpRequest: BrowseShotHelpRequestLike) {
  if (helpRequest.userIssueMask !== null && Number.isInteger(helpRequest.userIssueMask)) {
    return helpRequest.userIssueMask & SPLIT_ISSUE_MASK_ALL;
  }
  if (helpRequest.issueMask > 0) {
    return helpRequest.issueMask & SPLIT_ISSUE_MASK_ALL;
  }
  if (helpRequest.systemIssueMask > 0) {
    return helpRequest.systemIssueMask & SPLIT_ISSUE_MASK_ALL;
  }
  for (const key of ["user_issue_mask", "issue_mask", "system_issue_mask"] as const) {
    if (!Object.prototype.hasOwnProperty.call(helpRequest.details || {}, key)) {
      continue;
    }
    const detailMask = Number(helpRequest.details?.[key]);
    if (Number.isInteger(detailMask)) {
      return detailMask & SPLIT_ISSUE_MASK_ALL;
    }
  }
  if (helpRequest.type === "under_split_candidate") {
    return SPLIT_ISSUE_UNDER;
  }
  if (isOverSplitHelpRequest(helpRequest)) {
    return SPLIT_ISSUE_OVER;
  }
  return 0;
}

export function helpRequestNeedsUserReview(helpRequest: BrowseShotHelpRequestLike) {
  return helpRequest.helpState === "user_need_to_help" ||
    (helpRequest.status === "open" && helpRequest.helpState !== "system_need_to_adjust");
}

export function shotSplitHelpIssueMask(shot: BrowseShotLike) {
  return shot.helpRequests.reduce(
    (mask, helpRequest) => mask | helpRequestIssueMask(helpRequest),
    0
  ) & SPLIT_ISSUE_MASK_ALL;
}

export function shotSplitHelpState(
  shot: BrowseShotLike,
  override?: SplitHelpReviewOverride
): BrowseShotHelpStateLike {
  if (override) {
    return override.helpState;
  }
  if (shot.helpRequests.some((helpRequest) => helpRequest.helpState === "ignored" || helpRequest.status === "ignored")) {
    return "ignored";
  }
  if (shot.helpRequests.some((helpRequest) => helpRequest.helpState === "system_need_to_adjust")) {
    return "system_need_to_adjust";
  }
  if (shot.helpRequests.some((helpRequest) => helpRequest.helpState === "user_need_to_help" || helpRequest.status === "open")) {
    return "user_need_to_help";
  }
  if (shot.helpRequests.some((helpRequest) => helpRequest.helpState === "resolved" || helpRequest.status === "confirmed_ok")) {
    return "resolved";
  }
  return "unidentified";
}

export function shotSplitHelpStateLabel(state: BrowseShotHelpStateLike) {
  switch (state) {
    case "user_need_to_help":
      return "user needs to help";
    case "system_need_to_adjust":
      return "system needs to adjust";
    case "resolved":
      return "resolved";
    case "ignored":
      return "ignored";
    case "unidentified":
    default:
      return "unidentified";
  }
}

export function helpRequestMatchesFilter(
  helpRequest: BrowseShotHelpRequestLike,
  filter: ActiveShotHelpFilterMode
) {
  if (filter === "ignored") {
    return helpRequest.helpState === "ignored" || helpRequest.status === "ignored";
  }
  if (!helpRequestNeedsUserReview(helpRequest)) {
    return false;
  }
  const issueMask = helpRequestIssueMask(helpRequest);
  if (filter === "review") {
    return true;
  }
  return filter === "over_split"
    ? splitIssueHasMerge(issueMask)
    : (issueMask & SPLIT_ISSUE_UNDER) !== 0;
}

export function shotMatchesActionableHelpFilter(
  shot: BrowseShotLike,
  filter: ActiveShotHelpFilterMode
) {
  if (filter === "ignored") {
    return shotSplitHelpState(shot) === "ignored";
  }
  if (filter === "review") {
    return shotSplitHelpState(shot) === "user_need_to_help";
  }
  return shot.helpRequests.some((helpRequest) => {
    if (!helpRequestNeedsUserReview(helpRequest) && helpRequest.helpState !== "system_need_to_adjust") {
      return false;
    }
    const issueMask = helpRequestIssueMask(helpRequest);
    return filter === "over_split"
      ? splitIssueHasMerge(issueMask)
      : (issueMask & SPLIT_ISSUE_UNDER) !== 0;
  });
}

export function countShotActionableHelpItems(
  shots: BrowseShotLike[],
  filter: ActiveShotHelpFilterMode
) {
  return new Set(
    shots
      .filter((shot) => shotMatchesActionableHelpFilter(shot, filter))
      .map((shot) => shot.shotId)
  ).size;
}

function helpDetailNumber(details: Record<string, unknown> | null, key: string) {
  const value = details?.[key];
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function overSplitIssueInterval(shot: BrowseShotLike, helpRequest: BrowseShotHelpRequestLike) {
  if (!isOverSplitHelpRequest(helpRequest)) {
    return splitIssueHasMerge(helpRequestIssueMask(helpRequest))
      ? { videoId: shot.videoId, start: shot.shotNo, end: shot.shotNo }
      : null;
  }
  const startShotNo = helpDetailNumber(helpRequest.details, "cluster_start_shot_no") ?? shot.shotNo;
  const endShotNo = helpDetailNumber(helpRequest.details, "cluster_end_shot_no") ?? shot.shotNo;
  return {
    videoId: shot.videoId,
    start: Math.min(startShotNo, endShotNo),
    end: Math.max(startShotNo, endShotNo)
  };
}

export function countShotHelpIssues(shots: BrowseShotLike[], filter: ActiveShotHelpFilterMode) {
  if (filter === "review" || filter === "ignored") {
    const seen = new Set<number>();
    for (const shot of shots) {
      if (shotSplitHelpState(shot) === (filter === "review" ? "user_need_to_help" : "ignored")) {
        seen.add(shot.shotId);
      }
    }
    return seen.size;
  }

  if (filter === "over_split") {
    const intervals = shots
      .flatMap((shot) => shot.helpRequests
        .filter((helpRequest) => helpRequestMatchesFilter(helpRequest, filter))
        .map((helpRequest) => overSplitIssueInterval(shot, helpRequest)))
      .filter((interval): interval is { videoId: number; start: number; end: number } => interval !== null)
      .sort((left, right) => left.videoId - right.videoId || left.start - right.start || left.end - right.end);
    let count = 0;
    let current: { videoId: number; start: number; end: number } | null = null;
    for (const interval of intervals) {
      if (!current || current.videoId !== interval.videoId || interval.start > current.end + 1) {
        count += 1;
        current = { ...interval };
      } else {
        current.end = Math.max(current.end, interval.end);
      }
    }
    return count;
  }

  const seen = new Set<string>();
  for (const shot of shots) {
    for (const helpRequest of shot.helpRequests) {
      if (helpRequestMatchesFilter(helpRequest, filter)) {
        seen.add(`${helpRequest.type}:${shot.shotId}`);
      }
    }
  }
  return seen.size;
}

export function summarizeShotHelp(shots: BrowseShotLike[]): ShotHelpSummary {
  const states: Record<BrowseShotHelpStateLike, number> = {
    unidentified: 0,
    user_need_to_help: 0,
    system_need_to_adjust: 0,
    resolved: 0,
    ignored: 0
  };
  const requestBuckets: Record<string, number> = {};
  for (const shot of shots) {
    states[shotSplitHelpState(shot)] += 1;
    for (const helpRequest of shot.helpRequests) {
      const bucket = `${helpRequest.helpState}:${helpRequestIssueMask(helpRequest)}:${helpRequest.type}`;
      requestBuckets[bucket] = (requestBuckets[bucket] || 0) + 1;
    }
  }
  return {
    footer: {
      unidentified: states.unidentified,
      review: countShotHelpIssues(shots, "review"),
      ignored: states.ignored,
      over: countShotHelpIssues(shots, "over_split"),
      under: countShotHelpIssues(shots, "under_split_candidate"),
      applyGbdt: states.system_need_to_adjust
    },
    states,
    requestBuckets
  };
}
